<template>
    <b-container fluid>
        <b-row>
            <b-col cols="12">
                <admin-page-header title="이메일 템플릿 관리" description="발송 이메일의 템플릿을 관리합니다.">
                    <template #actions>
                        <b-button variant="primary" @click="goToNewEditor">
                            <AppIcon name="plus" class="mr-1"/>새 템플릿
                        </b-button>
                    </template>
                </admin-page-header>
            </b-col>
        </b-row>

        <!-- 검색 -->
        <b-row class="mb-3"><b-col cols="12">
        <b-card no-body bg-variant="white" class="p-3">
            <b-row>
                <b-col cols="12" md="3" class="mb-2 mb-md-0">
                    <b-form-input
                        v-model="searchForm.keyword"
                        placeholder="템플릿 이름 검색"
                        size="sm"
                        :readonly="loading"
                        @keyup.enter="searchData"
                    />
                </b-col>
                <b-col cols="6" md="3" class="mb-2 mb-md-0">
                    <b-form-select
                        v-model="searchForm.scenario"
                        :options="scenarioOptions"
                        size="sm"
                        :disabled="loading"
                    />
                </b-col>
                <b-col cols="6" md="3" class="mb-2 mb-md-0">
                    <b-form-select
                        v-model="searchForm.subDomain"
                        :options="subDomainOptions"
                        size="sm"
                        :disabled="loading"
                    />
                </b-col>
                <b-col cols="12" md="3" class="text-md-right">
                    <b-button variant="primary" size="sm" @click="searchData" :disabled="loading" class="mr-1">검색</b-button>
                    <b-button variant="outline-secondary" size="sm" @click="resetSearch" :disabled="loading">초기화</b-button>
                </b-col>
            </b-row>
        </b-card>
        </b-col></b-row>

        <!-- 목록 -->
        <b-row><b-col cols="12">
        <b-card no-body bg-variant="white" class="p-3">
            <!-- 이미 보이는 목록이 있는데 새 조회가 실패하면 목록은 두고 이전 결과임을 알린다 -->
            <b-alert v-if="loadError && templates.length" show variant="warning" class="d-flex align-items-center justify-content-between">
                <span>목록을 새로 불러오지 못했습니다({{ loadError }}). 아래는 이전 결과입니다.</span>
                <b-button size="sm" variant="outline-secondary" class="ml-2 flex-shrink-0" @click="fetchData">다시 시도</b-button>
            </b-alert>
            <b-table
                :items="templates"
                :fields="columns"
                responsive
                hover
                striped
                show-empty
                :busy="loading"
            >
                <template #table-busy>
                    <div class="text-center my-3">
                        <b-spinner variant="primary" />
                    </div>
                </template>

                <!-- 조회 실패를 '템플릿 없음'으로 보이지 않게 -->
                <template #empty>
                    <PageEmptyComponent compact text="등록된 템플릿이 없습니다." :error="loadError" @retry="fetchData" />
                </template>

                <template #cell(scenario_code)="{ item }">
                    <span :title="item.scenario_code">{{ scenarioLabel(item.scenario_code) }}</span>
                </template>

                <template #cell(label)="{ item }">
                    {{ item.label }}
                    <b-badge v-if="item.is_fallback" variant="info" class="ml-1">기본</b-badge>
                </template>

                <template #cell(contexts)="{ item }">
                    <span v-if="item.is_fallback" class="text-muted">없음 (기본)</span>
                    <div v-else class="condition-cell">
                        <div v-for="(v, k) in item.contexts" :key="k" :title="`${contextKeyLabel(k)}: ${v}`" class="condition-row">
                            <span class="condition-key">{{ contextKeyLabel(k) }}</span>
                            <span
                                v-for="(part, i) in conditionValueParts(k, v)"
                                :key="`${k}-${i}`"
                                :title="part.id ? `#${part.id}` : part.text"
                                class="condition-chip"
                            >{{ part.text }}</span>
                        </div>
                    </div>
                </template>

                <template #cell(locales)="{ item }">
                    <b-badge
                        v-for="locale in (item.locales || [])"
                        :key="locale"
                        variant="outline-secondary"
                        class="mr-1"
                    >{{ locale }}</b-badge>
                </template>

                <template #cell(updated_at)="{ item }">
                    {{ formatDate(item.updated_at) }}
                </template>

                <template #cell(action)="{ item }">
                    <b-button
                        variant="outline-primary"
                        size="sm"
                        class="mr-1"
                        @click="goToEditor(item.id)"
                    >편집</b-button>
                    <b-button
                        variant="outline-secondary"
                        size="sm"
                        title="모든 언어·발송 상황·조건을 채운 새 템플릿 화면을 엽니다. '생성'을 누르기 전에는 만들어지지 않습니다."
                        @click="goToDuplicate(item.id)"
                    >복제</b-button>
                </template>
            </b-table>

            <div class="d-flex justify-content-center mt-3" v-if="pagination.totalRows > pagination.perPage">
                <b-pagination
                    v-model="pagination.currentPage"
                    :total-rows="pagination.totalRows"
                    :per-page="pagination.perPage"
                    @change="onPageChange"
                />
            </div>
        </b-card>
        </b-col></b-row>
    </b-container>
</template>

<script>
import { dashboardColumns, CONTEXT_KEYS, sortScenarios } from './MailEditor/data'
// ⛔ CommonJS 헬퍼는 이 코드베이스 관례대로 default import 로 받는다.
import conditionLabels from './_common/conditionLabels'
import { getErrorMessage } from '@core/utils/api/errorMessage'
import PageEmptyComponent from '@shared_components/admin/components/common/PageEmptyComponent.vue'

// 검색 입력 필드 → URL 키. 편집기가 이 query 를 그대로 들고 갔다가 목록으로 돌려준다(키 이름 유지).
const FILTER_KEYS = { keyword: 'keyword', scenario: 'scenario', subDomain: 'sub_domain' }
const parsePage = v => (typeof v === 'string' && /^\d+$/.test(v) && Number(v) >= 1 ? Number(v) : 1)

export default {
    name: 'MailDashboard',
    components: { PageEmptyComponent },
    data() {
        return {
            loading: false,
            loadError: '',
            // 마지막 목록 요청 순번 — 늦게 온 이전 페이지·조건의 응답을 버린다
            requestSeq: 0,
            templates: [],
            columns: dashboardColumns,
            // 검색을 누르기 전 입력 초안. 화면에 적용된 조건은 URL(appliedQuery)이다.
            searchForm: {
                keyword: '',
                scenario: '',
                subDomain: '',
            },
            pagination: {
                currentPage: 1,
                perPage: 20,
                totalRows: 0,
            },
            scenarios: [],
            subDomains: [],
            // labelKey → { label, sub } | null(찾을 수 없음). 페이지를 넘겨도 유지해 같은 ID 를 다시 묻지 않는다.
            conditionNames: {},
            // 권한 없음(403)이면 더 묻지 않고 원문 그대로 보여 준다. 일시적 실패는 다음 조회 때 다시 시도.
            conditionLookupForbidden: false,
        }
    },
    computed: {
        scenarioOptions() {
            const opts = [{ value: '', text: '전체 시나리오' }]
            this.scenarios.forEach(s => {
                opts.push({ value: s.code, text: s.label || s.code })
            })
            return opts
        },
        subDomainOptions() {
            const opts = [{ value: '', text: '전체 서브도메인' }]
            this.subDomains.forEach(sd => {
                opts.push({ value: sd, text: sd })
            })
            return opts
        },
        // URL 에서 읽은 적용 조건. 빈 값·1페이지는 키를 뺀다.
        appliedQuery() {
            const q = this.$route.query || {}
            const out = {}
            Object.values(FILTER_KEYS).forEach(key => {
                if (typeof q[key] === 'string' && q[key]) out[key] = q[key]
            })
            const page = parsePage(q.page)
            if (page > 1) out.page = String(page)
            return out
        },
    },
    watch: {
        // 사이드바 재클릭·뒤로가기처럼 같은 화면에서 query 만 바뀌어도 화면을 주소에 맞춘다
        // 페이지만 바뀌었으면 검색칸의 입력 중인 초안은 그대로 둔다
        '$route.query'(to, from) {
            if (this.$route.name !== 'MailDashboard') return
            const filtersChanged = Object.values(FILTER_KEYS).some(k => (to || {})[k] !== (from || {})[k])
            this.initFromRouteQuery(filtersChanged)
            this.fetchData()
        },
    },
    mounted() {
        this.initFromRouteQuery()
        this.loadScenarios()
        this.loadSubDomains()
        this.fetchData()
    },
    beforeDestroy() {
        // 화면을 떠난 뒤 온 응답이 다른 화면 주소를 고치지 않게
        this.requestSeq++
    },
    methods: {
        initFromRouteQuery(syncForm = true) {
            const q = this.appliedQuery
            if (syncForm) {
                Object.entries(FILTER_KEYS).forEach(([field, key]) => {
                    this.searchForm[field] = q[key] || ''
                })
            }
            this.pagination.currentPage = parsePage(q.page)
        },
        // 사용자 조작은 push. 주소가 이미 같으면 감시가 돌지 않으니 직접 다시 조회한다.
        navigate(query) {
            const current = this.$route.query || {}
            const same = Object.keys(query).length === Object.keys(current).length
                && Object.keys(query).every(k => current[k] === query[k])
            if (same) return this.fetchData()
            this.$router.push({ query }).catch(() => {})
        },
        async loadScenarios() {
            try {
                const res = await this.$axios.get('/mail/scenario')
                this.scenarios = sortScenarios(res.data || [])
            } catch (e) {
                console.error(e)
                this.$EventBus.$emit('toast-failure', '조회 오류', '발송 상황 목록을 불러오지 못했습니다. 새로고침 후 다시 시도해주세요.')
            }
        },
        // 조건(context)에 실제로 쓰인 sub_domain 값으로 드롭다운 구성 (하드코딩 X).
        // 백엔드가 같은 값(템플릿 조건의 sub_domain distinct)을 브랜드 어휘로 주므로 전체 템플릿을 받지 않는다.
        async loadSubDomains() {
            try {
                const { data } = await this.$axios.get('/admin/project-brands/vocabulary')
                // ApiResponse 봉투({success, code, data}) 또는 인터셉터가 벗긴 payload
                const p = data && typeof data === 'object' && 'success' in data && 'data' in data ? data.data : data
                this.setSubDomains((p && p.brands) || [])
            } catch (e) {
                const status = e && e.response && e.response.status
                if (status === 401) return
                // 어휘 API 는 최상위 관리자 전용 — 권한이 없으면 예전처럼 템플릿에서 모은다(500건까지)
                if (status === 403) return this.loadSubDomainsFromTemplates()
                console.error(e)
                this.$EventBus.$emit('toast-failure', '조회 오류', '서브도메인 목록을 불러오지 못했습니다. 새로고침 후 다시 시도해주세요.')
            }
        },
        async loadSubDomainsFromTemplates() {
            try {
                const res = await this.$axios.get('/mail/template', { params: { page: 1, size: 500 } })
                const values = []
                ;(res.data.results || []).forEach(t => {
                    const sd = t.contexts && t.contexts.sub_domain
                    if (sd) values.push(sd)
                })
                this.setSubDomains(values)
            } catch (e) {
                console.error(e)
                this.$EventBus.$emit('toast-failure', '조회 오류', '서브도메인 목록을 불러오지 못했습니다. 새로고침 후 다시 시도해주세요.')
            }
        },
        setSubDomains(values) {
            this.subDomains = [...new Set(values)].sort((a, b) => String(a).localeCompare(String(b)))
        },
        // 조회 조건은 입력 초안이 아니라 URL 에서 만든다 — 검색을 누르지 않은 입력이 페이지 이동 때 몰래 적용되지 않게
        async fetchData() {
            const seq = ++this.requestSeq
            const query = this.appliedQuery
            const page = parsePage(query.page)
            this.loading = true
            this.loadError = ''
            try {
                const params = {
                    page,
                    size: this.pagination.perPage,
                }
                if (query.keyword) params.keyword = query.keyword
                if (query.scenario) params.scenario = query.scenario
                if (query.sub_domain) params.context = 'sub_domain:' + query.sub_domain

                const res = await this.$axios.get('/mail/template', { params })
                if (seq !== this.requestSeq) return
                const data = res.data || {}
                const total = data.total || 0
                // 오래된 링크의 페이지가 마지막 페이지를 넘으면 마지막 페이지로 (자동 보정이라 replace, 감시가 다시 조회)
                const last = Math.max(1, Math.ceil(total / this.pagination.perPage))
                if (total > 0 && page > last) {
                    const fixed = { ...query }
                    delete fixed.page
                    if (last > 1) fixed.page = String(last)
                    this.$router.replace({ query: fixed }).catch(() => {})
                    return
                }
                this.templates = data.results || []
                this.pagination.totalRows = total
                this.resolveConditionLabels()
            } catch (e) {
                if (seq !== this.requestSeq) return
                // 401 은 인터셉터가 로그인으로 보낸다
                if (e && e.response && e.response.status === 401) return
                console.error(e)
                this.loadError = getErrorMessage(e, '템플릿 목록을 불러오지 못했습니다.')
            } finally {
                if (seq === this.requestSeq) this.loading = false
            }
        },
        searchData() {
            if (this.loading) return
            const query = {}
            Object.entries(FILTER_KEYS).forEach(([field, key]) => {
                if (this.searchForm[field]) query[key] = this.searchForm[field]
            })
            this.navigate(query)
        },
        resetSearch() {
            Object.keys(FILTER_KEYS).forEach(field => { this.searchForm[field] = '' })
            this.navigate({})
        },
        // 페이지 이동은 적용된 조건 그대로 페이지만 바꾼다(뒤로가기 = 이전 페이지)
        onPageChange(page) {
            const query = { ...this.appliedQuery }
            delete query.page
            if (page > 1) query.page = String(page)
            this.navigate(query)
        },
        formatDate(dt) {
            if (!dt) return ''
            return new Date(dt).toLocaleDateString('ko-KR', {
                year: 'numeric', month: '2-digit', day: '2-digit',
            })
        },
        contextKeyLabel(k) {
            const found = CONTEXT_KEYS.find(c => c.value === k)
            return found ? found.label : k
        },
        entityOf(k) {
            const found = CONTEXT_KEYS.find(c => c.value === k)
            return found ? found.entity : ''
        },
        conditionValueParts(k, v) {
            return conditionLabels.conditionValueParts(this.entityOf(k), v, this.conditionNames)
        },
        // 화면에 있는 행들의 ID 를 엔티티별 한 번씩만 물어 이름으로 바꾼다. 실패하면 원문 표시 그대로 둔다.
        async resolveConditionLabels() {
            if (this.conditionLookupForbidden) return
            const requests = conditionLabels.collectLookupRequests(this.templates, this.entityOf, this.conditionNames)
            if (!requests.length) return
            await Promise.all(requests.map(async ({ entity, ids }) => {
                try {
                    const { data } = await this.$axios.get(`/mail/lookup/${entity}`, { params: { ids: ids.join(',') } })
                    const found = {}
                    ;((data && data.items) || []).forEach(item => {
                        found[item.value] = { label: item.label, sub: item.sub }
                    })
                    // 응답이 오는 대로 병합한다. 스냅샷을 통째로 덮으면 페이지를 빨리 넘길 때 늦게 온 응답이 먼저 온 이름을 지운다.
                    const delta = {}
                    ids.forEach(id => {
                        delta[conditionLabels.labelKey(entity, id)] = found[id] || null
                    })
                    this.conditionNames = { ...this.conditionNames, ...delta }
                } catch (e) {
                    if (e && e.response && e.response.status === 403) this.conditionLookupForbidden = true
                }
            }))
        },
        scenarioLabel(code) {
            const found = this.scenarios.find(s => s.code === code)
            return found ? (found.label || code) : code
        },
        goToNewEditor() {
            this.$router.push({ name: 'MailEditorNew', query: this.appliedQuery })
        },
        goToEditor(id) {
            this.$router.push({ name: 'MailEditorEdit', params: { id }, query: this.appliedQuery })
        },
        goToDuplicate(id) {
            this.$router.push({ name: 'MailEditorNew', query: { ...this.appliedQuery, copy_from: String(id) } })
        },
    },
}
</script>

<style scoped>
/* 조건 열: 키 한 줄에 값 칩 나열. 배지(가운데 정렬·한 줄)로는 이름이 길어지면 읽기 어렵다. */
.condition-cell {
    text-align: left;
}
.condition-row + .condition-row {
    margin-top: 4px;
}
.condition-key {
    display: inline-block;
    min-width: 76px;
    margin-right: 6px;
    color: #6c757d;
    font-size: 12px;
}
.condition-chip {
    display: inline-block;
    margin: 0 4px 2px 0;
    padding: 1px 6px;
    font-size: 12px;
    line-height: 1.5;
    background-color: #f1f3f5;
    border: 1px solid #e3e6ea;
    border-radius: 3px;
}
</style>
